import * as React from 'react';
import { styled } from '@glitz/react';
import { translate, useCurrentCountry, useDynamicData, useMatchMobile } from '@avensia/excite';
import { Theme } from 'webapp/theme';
import ProductVariantSelector from './ProductVariantsSelector';
import { createMaxMobileViewportDecorator, createMaxSmallViewportDecorator } from 'webapp/viewport';
import { TestIdProps } from '@avensia/test-automation';
import {
  OriginalPrice,
  Price,
  PriceInfoHeader,
  PriceInfoHeaderWrapper,
  PriceInfoText,
  PriceInfoWrapper,
} from './Price';
import { addToCart } from 'webapp/product-and-package-utils';
import { PackageParts } from './PackageParts';
import { useCallback } from 'react';
import { useVariantSelector } from 'webapp/variant-selectors/ProductVariantsSelectorProvider';
import { getSelectedParts, useCalcDeliveryTime, useCalcPrice } from 'webapp/variant-selectors/use-bundle-selectors';
import KlarnaOnSiteMessagingWrapper from 'webapp/components/klarna-on-site-messaging-wrapper';
import { WishlistButton } from './FavoriteButton';
import { BulletList } from './BulletList';
import { Highlights } from './Highlights';
import { InfoBox } from './InfoBox';
import { ShippingInfo } from './ShippingInfo';
import { StoreInfo } from './StoreInfo';
import { ProductAccessoryCards } from './ProductAccessoryCards';
import { addAccessoryToCart } from './commands';
import { useQuantity } from './use-quantity';
import { StockStatus as StockStatusIndicator } from 'nordicnest/product/StockStatus';
import { FeedbackStandaloneButton } from 'webapp/components/button';
import IngridPdpWidget from 'webapp/pages/ProductPage/IngridPdpWidget';
import { FieldSize } from 'webapp/components/field-size';
import { ResetButton } from 'webapp/components/button/button';
import { NumberField } from 'webapp/components/form/number-field';
import { darkGrey, white } from 'webapp/shared/sharedTheme';
import { translationExists } from 'webapp/utils/translations';
import Modal from 'webapp/components/modal';
import { useCustomerType } from '@avensia/excite-commercetools';
import { DiscountInfo } from './DiscountInfo';
import ProductPageBadges from './ProductPageBadges';
import { NnTestIdProps } from 'webapp/shared/test-ids';
import { useFormatPrice } from '@avensia/excite-ui';
import MaterialSamples from './MaterialSamples';
import { useNinetailedFeatureTest } from 'webapp/ninetailed/NinetailedProvider';

type PropType = NordicNest.Api.ProductPageApiModel & {
  showStoreAvailability?: () => void;
  showPriceMatchInfo?: () => void;
  showShippingInfo?: () => void;
  showProductBadgesFlyout: () => void;
  productBadges: NordicNest.Api.ProductBadgeApiModel[];
  openUpSellAccessories: () => void;
  scrollToSpecifications: () => void;
  accessories: NordicNest.Api.ProductAccessoryInfoApiModel[] | undefined;
  accessoriesToCart: (NordicNest.Api.PartialProductApiModel & { quantity: number })[] | undefined;
  updateAccessoriesAdded: (
    accessories: (NordicNest.Api.PartialProductApiModel & { quantity: number })[],
    remove?: boolean,
  ) => void;
  showStoreView?: boolean;
  materialSamples: NordicNest.Api.PartialProductApiModel[] | undefined;
};

export const AddToCart = styled(
  (
    props: NordicNest.Api.ProductPageApiModel & {
      openUpSellAccessories: () => void;
      accessoriesToCart: (NordicNest.Api.PartialProductApiModel & { quantity: number })[] | undefined;
    },
  ) => {
    const isMobile = useMatchMobile();
    const { quantity, updatedQuantity } = useQuantity();

    React.useEffect(() => {
      updatedQuantity(1);
    }, [props.productId, updatedQuantity]);

    const clampQuantity = useCallback((quantity: number) => Math.min(Math.max(quantity, 1), 1000), []);
    const onChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
      evt => {
        updatedQuantity(clampQuantity(Number(evt.currentTarget.value)));
      },
      [clampQuantity, updatedQuantity],
    );
    const onAdd = useCallback(
      () => updatedQuantity(clampQuantity(quantity + 1)),
      [clampQuantity, quantity, updatedQuantity],
    );
    const onReduce = useCallback(
      () => updatedQuantity(clampQuantity(quantity - 1)),
      [clampQuantity, quantity, updatedQuantity],
    );

    React.useEffect(() => {
      setValidationMessage(undefined);
    }, [props.sku]);
    const [validationMessage, setValidationMessage] = React.useState<string>();
    const { productConfiguration, selectors } = useVariantSelector();

    const addToCartCallback = useCallback(() => {
      return Promise.allSettled([
        addToCart(setValidationMessage, props, quantity, 'pdp', getSelectedParts(selectors), productConfiguration).then(
          () => {
            if (!props?.accessoriesToCart?.length) {
              setTimeout(props.openUpSellAccessories, 500);
            }
          },
        ),
        props?.accessoriesToCart?.map(a =>
          addAccessoryToCart({
            sku: a.sku,
            accessoryToSku: props.sku,
            quantity: a.quantity * quantity,
            button_location: 'pdp',
          }),
        ),
      ]);
    }, [productConfiguration, props, quantity, selectors]);

    return (
      <>
        <AddToCartContainer>
          <Quantity
            size={FieldSize.Small}
            as={Theme.Transparent}
            value={quantity}
            min={1}
            max={1000}
            onChange={onChange}
            onAdd={onAdd}
            onReduce={onReduce}
          />
          <AddToCartButton
            {...TestIdProps.AddToCartButton}
            {...NnTestIdProps.AddToCartButtonPDP}
            disabled={!props.isBuyable}
            onClick={addToCartCallback}
            size={FieldSize.Small}
            as={Theme.ActionButton}
          >
            {translate(t => t.product.addToCartButtonText)}
          </AddToCartButton>
          {isMobile && <WishlistButton product={props} location="pdp" name="product_information" />}
        </AddToCartContainer>
        {!isMobile && <WishlistButton product={props} location="pdp" name="product_information" />}
      </>
    );
  },
);

export const ProductInformation = React.memo(function ProductInformation(props: PropType) {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [productDiscount] = useDynamicData<NordicNest.Api.ProductDiscountApiModel>({
    context: props.discountsContext,
  });
  const { isCompanyCustomer } = useCustomerType();
  const formatPrice = useFormatPrice();
  const modalRef = React.useRef<HTMLDivElement>(null);
  const isMobile = useMatchMobile();
  const { price } = useCalcPrice(props.price, props.packageKey);
  const { packageSlowestVariantDeliveryTime, packageWorstPartStatus, packageWorstStockStatus } = useCalcDeliveryTime(
    props.packageParts,
  );
  const currentCountry = useCurrentCountry();
  const { quantity } = useQuantity();
  const { variantKey, componentRef } = useNinetailedFeatureTest<HTMLDivElement>('physical-store-info');

  const hideStoreInfoForTest = variantKey === 'hide-store-info';
  const originalPrice = price?.regular;
  const comparePrice =
    props.price?.showComparePriceType === 'originalPrice' ? price?.regular : props.price?.lowestHistoricalPrice;
  const sameAsRef = originalPrice?.inclVat === props.price?.lowestHistoricalPrice?.inclVat;
  const showOriginalPrice = !(sameAsRef && props.price?.hideOriginalIfSame) && props.price?.showOriginalPrice;
  const comparePriceIsLowestHistoricalPrice = props.price?.showComparePriceType === 'referencePrice';

  const setShowModal = (show: boolean) => {
    setModalOpen(show);
  };

  return (
    <Container>
      <div ref={modalRef} style={{ position: isMobile ? 'unset' : 'relative' }}>
        <Modal
          show={modalOpen}
          header={translate(t => t.product.priceInformation)}
          onClose={() => setModalOpen(false)}
          parentElement={modalRef.current}
        >
          <PriceInfoWrapper>
            {showOriginalPrice && originalPrice && (
              <div>
                <PriceInfoHeaderWrapper>
                  <PriceInfoHeader>{translate(t => t.product.priceInfoRecommendedHeader)}</PriceInfoHeader>
                  <OriginalPrice css={{ fontSize: '14px' }} suppressHydrationWarning>
                    {isCompanyCustomer ? formatPrice(originalPrice.exclVat) : formatPrice(originalPrice.inclVat)}
                  </OriginalPrice>
                </PriceInfoHeaderWrapper>
                <PriceInfoText>{translate(t => t.product.priceInfoRecommendedText)}</PriceInfoText>
              </div>
            )}
            {props.price?.showComparePrice && comparePrice && (
              <div>
                <PriceInfoHeaderWrapper>
                  <PriceInfoHeader>
                    {comparePriceIsLowestHistoricalPrice
                      ? translate(t => t.product.priceInfoLowestPriceHeader)
                      : translate(t => t.product.priceInfoRecommendedHeader)}
                  </PriceInfoHeader>
                  <OriginalPrice
                    css={
                      props.price?.showComparePriceStrikethrough
                        ? { fontSize: '14px', textDecoration: 'line-through' }
                        : undefined
                    }
                    suppressHydrationWarning
                  >
                    {isCompanyCustomer ? formatPrice(comparePrice.exclVat) : formatPrice(comparePrice.inclVat)}
                  </OriginalPrice>
                </PriceInfoHeaderWrapper>
                <PriceInfoText>
                  {comparePriceIsLowestHistoricalPrice
                    ? translate(t => t.product.priceInfoLowestPriceText)
                    : translate(t => t.product.priceInfoRecommendedText)}
                </PriceInfoText>
              </div>
            )}
          </PriceInfoWrapper>
        </Modal>
      </div>
      <BadgesContainer>
        {productDiscount?.success && <DiscountInfo productDiscount={productDiscount} />}
        {props.productBadges.length > 0 && (
          <ProductPageBadges
            productBadges={props.productBadges}
            showProductBadgesFlyout={props.showProductBadgesFlyout}
          />
        )}
      </BadgesContainer>
      {props.availableInCountry && (
        <PriceRow>
          <Price price={price} config={props.price} showPriceMatch={props.showPriceMatch} setShowModal={setShowModal} />
          {props.showPriceMatch && (
            <PriceMatchInfo onClick={props.showPriceMatchInfo}>
              <PriceMatchText>{translate(t => t.productPage.priceMatchInfoText)}</PriceMatchText>
              <PriceMatchButton>{translate(t => t.productPage.priceMatchButtonText)}</PriceMatchButton>
            </PriceMatchInfo>
          )}
        </PriceRow>
      )}
      <PriceRow>
        {props.price?.showComparePrice &&
          props.price?.showComparePriceType === 'originalPrice' &&
          translationExists(translate(t => t.productPage.recommendedPriceDisclaimer)) && (
            <RecommendedPriceDisclaimer>
              {translate(t => t.productPage.recommendedPriceDisclaimer)}
            </RecommendedPriceDisclaimer>
          )}
      </PriceRow>
      {!props.quickBuyPackage && (
        <>
          {props.packageParts && props.packageParts.length > 1 ? (
            <PackageParts packageParts={props.packageParts} variants={props.variants} />
          ) : (
            <ProductVariantSelector />
          )}
        </>
      )}
      {props?.accessories && (
        <ProductAccessoryCards
          quantity={quantity}
          updateAccessoriesAdded={props.updateAccessoriesAdded}
          accessories={props.accessories}
        />
      )}
      {!isMobile && <AddToCart {...props} />}
      {!isMobile && <KlarnaOnSiteMessagingWrapper price={props.price} />}
      <MaterialSamples materialSamples={props.materialSamples} />
      <ProductHandlingGrouping ref={componentRef}>
        <StockStatusContainer>
          {props.availableInCountry ? (
            <StockStatus
              pdpstyling
              deliveryTime={packageSlowestVariantDeliveryTime?.deliveryText ?? props.deliveryTime}
              variantStatus={packageWorstPartStatus ?? props.variantStatus}
              stockStatus={packageWorstStockStatus ?? props.stockStatus}
            />
          ) : (
            <NotAvailableInCountry>
              {translate(t => t.productPage.productNotAvailableInCountry, { ['country']: currentCountry.localName })}
            </NotAvailableInCountry>
          )}
        </StockStatusContainer>
        {
          // TODO: Investigate why ProductInformation re-renders multiple times when changin color on PDP, and why some props are not set for the first re-renders.
          props.ingridProductPageConfig &&
            (props.ingridProductPageConfig.auth_token ? (
              <IngridContainer>
                <IngridPdpWidget {...props.ingridProductPageConfig} />
              </IngridContainer>
            ) : (
              <ShippingInfo
                showShippingInfo={props.showShippingInfo}
                freeShipping={props.freeShipping}
                lowestShippingPrice={props.lowestShippingPrice}
              />
            ))
        }
        {props?.showStoreView && !hideStoreInfoForTest && (
          <StoreInfo
            showStoreAvailability={props.showStoreAvailability}
            storesAvailability={props.storesAvailability}
          />
        )}
      </ProductHandlingGrouping>
      {props.highlights?.length > 1 && <Highlights highlights={props.highlights} />}
      <BulletList bullets={props.bullets} scrollToSpecifications={props.scrollToSpecifications} />
      <InfoBox infoBox={props.infobox} />
    </Container>
  );
});

const RecommendedPriceDisclaimer = styled.div({
  fontSize: t => t.fontSize.xxs,
  fontWeight: 400,
  color: darkGrey,
});

export const Quantity = styled(NumberField, {
  fontSize: t => t.sharedFontSize.xs,
  backgroundColor: white,
  outline: { width: 1, style: 'solid', color: t => t.borderStrongColor, offset: -1 },
});

const ProductHandlingGrouping = styled.div({ marginBottom: t => t.sharedSpacing.xl });

const Container = styled.div(
  createMaxSmallViewportDecorator({
    padding: { x: t => t.sharedSpacing.m },
  }),
  {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    fontWeight: 300,
  },
);

const StockStatusContainer = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  position: 'relative',
  padding: { xy: t => t.sharedSpacing.m },
  border: { xy: { style: 'solid', color: t => t.borderStrongColor, width: 1 } },
});

const PriceRow = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  margin: { bottom: t => t.sharedSpacing.m },
});

const PriceMatchInfo = styled(ResetButton, {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  ':focus': {
    outlineWidth: 0,
  },
});

const PriceMatchText = styled.span({
  fontSize: t => t.sharedFontSize.xs,
  fontWeight: 450,
});

const PriceMatchButton = styled.span({
  fontSize: t => t.sharedFontSize.xs,
  textDecoration: 'underline',
});

const StockStatus = styled(StockStatusIndicator, {
  color: 'black',
  fontSize: t => t.sharedFontSize.xs,
  marginBottom: 0,
});

const NotAvailableInCountry = styled.div({
  fontSize: t => t.sharedFontSize.xs,
});

const AddToCartContainer = styled.div(
  createMaxMobileViewportDecorator({
    gridTemplateColumns: '85px auto 40px',
  }),
  {
    display: 'grid',
    gridTemplateColumns: '85px auto',
    columnGap: t => t.sharedSpacing.xs,
    margin: { bottom: t => t.sharedSpacing.xs },
  },
);

const AddToCartButton = styled(FeedbackStandaloneButton, {
  fontWeight: 450,
  fontSize: t => t.sharedFontSize.xs,
});

const IngridContainer = styled.div({
  padding: { xy: t => t.sharedSpacing.s },
  border: { xy: { style: 'solid', color: t => t.borderStrongColor, width: 1 }, top: { style: 'none' } },
});

const BadgesContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: t => t.sharedSpacing.m,
  marginBottom: t => t.sharedSpacing.xl,
  ':empty': {
    marginBottom: 0,
  },
});
